import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    const shouldRemove = minusButton.dataset.removeAtMin === 'true' && value <= min;
    this.#applyMinusButtonMode(minusButton, shouldRemove);

    // Cart buttons are always dynamically managed
    minusButton.disabled = !shouldRemove && value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Toggles the minus button between "decrease quantity" and "remove line" presentation.
   * Runs on every local quantity change (not just after a section re-render), so the swap
   * happens instantly instead of waiting on the cart's AJAX round trip.
   * @param {HTMLButtonElement} minusButton
   * @param {boolean} shouldRemove
   */
  #applyMinusButtonMode(minusButton, shouldRemove) {
    // Snippet only renders the dual-icon markup when remove_at_min was passed in.
    if (minusButton.dataset.removeAtMin !== 'true') return;

    minusButton.classList.toggle('quantity-minus--remove', shouldRemove);

    const decreaseLabel = minusButton.querySelector('[data-quantity-minus-label]');
    const removeLabel = minusButton.querySelector('[data-quantity-remove-label]');
    const decreaseIcon = minusButton.querySelector('[data-quantity-minus-icon]');
    const removeIcon = minusButton.querySelector('[data-quantity-remove-icon]');

    if (decreaseLabel) decreaseLabel.style.display = shouldRemove ? 'none' : '';
    if (removeLabel) removeLabel.style.display = shouldRemove ? '' : 'none';
    if (decreaseIcon) decreaseIcon.style.display = shouldRemove ? 'none' : '';
    if (removeIcon) removeIcon.style.display = shouldRemove ? '' : 'none';

    const lineNumber = this.refs.quantityInput?.dataset.cartLine;
    if (shouldRemove && lineNumber) {
      minusButton.setAttribute('on:click', `cart-items-component/onLineItemRemove/${lineNumber}`);
    } else {
      minusButton.setAttribute('on:click', '/decreaseQuantity');
    }
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
